import React, { useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Typography } from "@mui/joy";
import { ReactComponent as CalendarIcon } from "../../../assets/svgs/calendarIcon.svg";
import { fromZonedTime } from "date-fns-tz";
import { PACIFIC_TIMEZONE } from "../../../constants/index";
import { convertUTCToPacific } from "../../../utils/helpers";

interface DateFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  setDayStartUtc?: (value: string) => void;
  setDayEndUtc?: (value: string) => void;
  minDate?: Date;
  maxDate?: Date;
}

const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder }, ref) => (
    <input
      type="text"
      onClick={onClick}
      ref={ref}
      value={value}
      placeholder={placeholder}
      readOnly
      className="custom-date-input placeholder:!text-[#7B7F85]"
    />
  )
);

const convertPacificMidnightToUTC = (date: Date): string => {
  const pacificMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  const utc = fromZonedTime(pacificMidnight, PACIFIC_TIMEZONE);
  return utc.toISOString();
};

const getPacificDayRangeUTC = (date: Date): { startUtc: string; endUtc: string } => {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0);
  return {
    startUtc: fromZonedTime(start, PACIFIC_TIMEZONE).toISOString(),
    endUtc: fromZonedTime(end, PACIFIC_TIMEZONE).toISOString()
  };
};

const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  setDayStartUtc,
  setDayEndUtc,
  minDate,
  maxDate,
}) => {
  const datePickerRef = useRef<DatePicker>(null);
  const dateValue = convertUTCToPacific(value);

  const handleDateChange = (selectedDate: Date | null) => {
    if (!selectedDate) {
      onChange("");
      if (setDayStartUtc) setDayStartUtc("");
      if (setDayEndUtc) setDayEndUtc("");
      return;
    }

    const utcTimestamp = convertPacificMidnightToUTC(selectedDate);
    onChange(utcTimestamp);

    const { startUtc, endUtc } = getPacificDayRangeUTC(selectedDate);
    if (setDayStartUtc) setDayStartUtc(startUtc);
    if (setDayEndUtc) setDayEndUtc(endUtc);
  };

  return (
    <Box>
      <Typography mb={1} level="label">
        Date
      </Typography>
      <Box sx={{ position: "relative", width: "100%", marginBottom: "8px" }}>
        <DatePicker
          ref={datePickerRef}
          selected={dateValue}
          onChange={handleDateChange}
          dateFormat="MM/dd/yyyy"
          placeholderText={placeholder}
          customInput={<CustomInput placeholder={placeholder} />}
          popperPlacement="bottom-start"
          minDate={minDate}
          maxDate={maxDate}
        />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: "16px",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        >
          <CalendarIcon />
        </Box>
      </Box>
    </Box>
  );
};

export default DateField;
